import type { FC } from 'react';

import type { AdminPaymentRecord } from '@/types/payment.types';
import { formatRupeesExact } from '@/utils/currency.util';

import { COLLECTION_STATUS_LABEL, SETTLEMENT_STATUS_LABEL } from '../constants';

interface PaymentsTableProps {
  records: AdminPaymentRecord[];
  onSelect: (record: AdminPaymentRecord) => void;
}

const Pill: FC<{ label: string; tone: string }> = ({ label, tone }) => (
  <span className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${tone}`}>
    {label}
  </span>
);

const PaymentsTable: FC<PaymentsTableProps> = ({ records, onSelect }) => (
  <div>
    {/* Desktop table */}
    <table className="hidden w-full text-left md:table">
      <thead>
        <tr className="border-b border-[var(--color-divider)] text-[11px] uppercase tracking-wide text-[var(--color-ink-muted)]">
          <th className="px-4 py-3 font-semibold">Code</th>
          <th className="px-4 py-3 font-semibold">Borrower → Owner</th>
          <th className="px-4 py-3 font-semibold">Amount</th>
          <th className="px-4 py-3 font-semibold">Collection</th>
          <th className="px-4 py-3 font-semibold">Settlement</th>
          <th className="px-4 py-3 font-semibold">Created</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r) => (
          <tr
            key={r.id}
            onClick={() => onSelect(r)}
            className="cursor-pointer border-b border-[var(--color-divider)] hover:bg-[var(--color-surface-muted)]"
          >
            <td className="px-4 py-3">
              <span className="font-mono text-[12px] font-bold text-[var(--color-ink)]">
                {r.paymentCode}
              </span>
              <span className="block text-[11px] text-[var(--color-ink-muted)]">{r.bookingCode}</span>
            </td>
            <td className="px-4 py-3 text-[12px] text-[var(--color-ink)]">
              {r.borrower.name} → {r.owner.name}
              {r.campusName ? (
                <span className="block text-[11px] text-[var(--color-ink-muted)]">{r.campusName}</span>
              ) : null}
            </td>
            <td className="px-4 py-3 text-[13px] font-bold text-[var(--color-ink)]">
              {formatRupeesExact(r.amounts.borrowerTotalPaise)}
            </td>
            <td className="px-4 py-3">
              <Pill {...COLLECTION_STATUS_LABEL[r.collectionStatus]} />
            </td>
            <td className="px-4 py-3">
              <Pill {...SETTLEMENT_STATUS_LABEL[r.settlementStatus]} />
            </td>
            <td className="px-4 py-3 text-[11px] text-[var(--color-ink-muted)]">
              {new Date(r.createdAt).toLocaleDateString()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>

    {/* Mobile cards */}
    <div className="flex flex-col gap-3 p-4 md:hidden">
      {records.map((r) => (
        <button
          key={r.id}
          type="button"
          onClick={() => onSelect(r)}
          className="flex flex-col gap-2 rounded-xl border border-[var(--color-divider)] bg-white p-4 text-left"
        >
          <div className="flex items-center justify-between">
            <span className="font-mono text-[12px] font-bold text-[var(--color-ink)]">
              {r.paymentCode}
            </span>
            <span className="text-[13px] font-bold text-[var(--color-ink)]">
              {formatRupeesExact(r.amounts.borrowerTotalPaise)}
            </span>
          </div>
          <p className="text-[12px] text-[var(--color-ink)]">
            {r.borrower.name} → {r.owner.name}
          </p>
          <div className="flex gap-2">
            <Pill {...COLLECTION_STATUS_LABEL[r.collectionStatus]} />
            <Pill {...SETTLEMENT_STATUS_LABEL[r.settlementStatus]} />
          </div>
        </button>
      ))}
    </div>
  </div>
);

export default PaymentsTable;
